import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';
import SectionTrustBar from '../../components/feature/SectionTrustBar';
import SectionAIAnswer from '../../components/feature/SectionAIAnswer';
import SectionProcess from '../../components/feature/SectionProcess';
import SectionServiceAreas from '../../components/feature/SectionServiceAreas';
import SectionTestimonials from '../../components/feature/SectionTestimonials';
import SectionFAQ from '../../components/feature/SectionFAQ';
import SectionFinalCTA from '../../components/feature/SectionFinalCTA';
import ServicesDetail from './components/ServicesDetail';

const trustItems = [
  { value: '4', label: 'Event Categories', icon: 'ri-layout-grid-line' },
  { value: '300', label: 'Max Guests', icon: 'ri-group-line' },
  { value: '1', label: 'Exclusive Event/Day', icon: 'ri-calendar-check-line' },
  { value: '6k+', label: 'Sq Ft Indoor', icon: 'ri-building-2-line' },
];

const aiAnswers = [
  {
    question: 'What types of events does Red Door Ranch host?',
    answer:
      'Red Door Ranch hosts weddings, receptions, private milestone events, corporate retreats, and outdoor ranch experiences. Each event receives exclusive access to the full property — no overlapping events, no distractions. The venue is fully equipped for both intimate gatherings and large celebrations up to 300 guests.',
  },
  {
    question: 'What is included in the Red Door Ranch event experience?',
    answer:
      'The venue provides access to 6,000+ sq ft of climate-controlled event space, 3,500 sq ft of covered patio, dedicated bridal and groom suites with full bathrooms, a grand staircase entrance, elegant landscaping, multiple layout configurations, and attentive on-site support throughout your event.',
  },
  {
    question: 'Does Red Door Ranch offer wedding coordination services?',
    answer:
      'Yes. Red Door Ranch offers wedding coordination services to help couples plan and execute their perfect day. Our experienced team works closely with you and your preferred vendors to ensure every element comes together seamlessly — from the rehearsal through the final send-off.',
  },
  {
    question: 'How does booking your event at Red Door Ranch work?',
    answer:
      'Begin with a private tour of the estate by calling (817) 504-8501 or submitting an inquiry online. From there, our team works with you to build a customized event plan, coordinate with your vendors, and ensure everything is perfectly in place for your special day.',
  },
];

const processSteps = [
  {
    number: '01',
    title: 'Reach Out',
    description: 'Call, email, or submit an inquiry to start a conversation about your event vision.',
  },
  {
    number: '02',
    title: 'Tour the Venue',
    description: 'Schedule a private walkthrough to experience the full 20-acre estate firsthand.',
  },
  {
    number: '03',
    title: 'Reserve Your Date',
    description: 'Secure your preferred date with a contract and begin building your perfect event.',
  },
  {
    number: '04',
    title: 'Celebrate',
    description: 'Arrive and enjoy — our team ensures everything is flawlessly in place for your big day.',
  },
];

const whyFeatures = [
  'Exclusive access — no shared events',
  'No time pressure all day',
  'Professional, caring team',
  'Stunning hilltop setting',
  '300 guest capacity',
  '20 private acres of property',
];

const serviceAreas = [
  { city: 'Joshua, TX', note: 'Venue Location' },
  { city: 'Fort Worth, TX', note: '20 min north' },
  { city: 'Burleson, TX' },
  { city: 'Cleburne, TX' },
  { city: 'Mansfield, TX' },
  { city: 'Crowley, TX' },
  { city: 'Alvarado, TX' },
  { city: 'Granbury, TX' },
  { city: 'Arlington, TX' },
  { city: 'Dallas, TX' },
];

const servicesFAQs = [
  {
    question: 'What wedding services does Red Door Ranch offer near Fort Worth?',
    answer:
      'Red Door Ranch provides full wedding venue rental including ceremony and reception spaces, bridal and groom suites, wedding coordination services, and event support. Located in Joshua, TX, the venue is a premier wedding destination 20 minutes south of Fort Worth with space for up to 300 guests.',
  },
  {
    question: 'Can Red Door Ranch accommodate outdoor wedding ceremonies?',
    answer:
      'Yes. The venue features beautiful outdoor ceremony spaces set against 20 acres of hilltop scenery, lush landscaping, and covered patio areas. Outdoor ceremonies can be complemented by seamless indoor reception space — offering the best of both worlds for your wedding day in Joshua, TX.',
  },
  {
    question: 'Does Red Door Ranch host corporate events and private parties?',
    answer:
      "Absolutely. Beyond weddings, Red Door Ranch is available for corporate retreats, team events, quinceañeras, anniversaries, reunions, and more. The venue's exclusive one-event-per-day policy ensures your gathering receives complete privacy and dedicated attention regardless of event type.",
  },
  {
    question: 'How do I check availability and pricing for the venue?',
    answer:
      'To check availability and receive current pricing for your event date, contact Red Door Ranch directly at (817) 504-8501 or submit an inquiry through our website. We recommend reaching out 12 to 18 months in advance to secure your preferred date.',
  },
];

function ServicesWhyChooseUs() {
  const { ref: leftRef, isVisible: leftVisible } = useInView();
  const { ref: rightRef, isVisible: rightVisible } = useInView({ threshold: 0.1 });

  return (
    <section className="bg-rdr-charcoal py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`slide-left ${leftVisible ? 'is-visible' : ''}`}
          >
            <div className="inline-flex items-center gap-4 mb-5">
              <span className="gold-divider" />
              <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">The Difference</p>
            </div>
            <h2 className="font-serif text-white text-4xl lg:text-5xl xl:text-6xl mb-6 leading-tight">
              One Venue. One Event.<br />
              <em className="italic text-rdr-gold">Infinite</em> Possibilities.
            </h2>
            <p className="text-white/55 font-sans text-base leading-relaxed mb-10 max-w-md">
              Our commitment to excellence means your event is never just another booking.
              At Red Door Ranch, your celebration receives our one undivided focus for the entire day.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-10">
              {whyFeatures.map((item, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xs text-rdr-gold" />
                  </div>
                  <span className="text-white/60 font-sans text-sm">{item}</span>
                </div>
              ))}
            </div>
            <Link
              to="/location#contact"
              className="inline-block bg-rdr-gold text-white font-sans text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-rdr-gold-light transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Book a Private Tour
            </Link>
          </div>
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`relative overflow-hidden w-full h-80 lg:h-[520px] slide-right ${rightVisible ? 'is-visible' : ''}`}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/11f1a32214d11fc45b081ee1ccb115aa.jpg"
              alt="Elegant wedding ceremony space at Red Door Ranch Joshua TX"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-rdr-charcoal/30 to-transparent" />
            {/* Floating badge */}
            <div className="absolute bottom-6 left-6 bg-rdr-dark/80 backdrop-blur-sm border border-rdr-gold/30 px-5 py-4">
              <p className="text-rdr-gold font-sans text-xs tracking-[0.2em] uppercase mb-1">One Event · All Day</p>
              <p className="text-white font-serif text-lg">100% Exclusive Access</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <main>
      {/* 1. Hero */}
      <section className="relative min-h-screen flex items-end justify-center overflow-hidden grain-overlay">
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=stunning-wedding-reception-with-guests-seated-at-round-tables-with-tall-floral-centerpieces-white-roses-greenery-soft-chandelier-lighting-from-above-warm-cream-and-gold-color-palette-luxury-event-venue-elegant-texas-ranch-ballroom-wide-angle&width=1920&height=1080&seq=services-hero-2026&orientation=landscape"
            alt="Wedding venue services at Red Door Ranch Joshua TX"
            className="w-full h-full object-cover object-center scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-rdr-dark via-rdr-dark/30 to-rdr-dark/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-rdr-dark/30 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-24 lg:pb-32 pt-36">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-8 hero-eyebrow">
              <span className="block w-12 h-px bg-rdr-gold" />
              <p className="text-rdr-gold text-xs tracking-[0.35em] uppercase font-sans font-medium">Weddings &amp; Events</p>
            </div>
            <h1 className="font-serif text-white text-5xl lg:text-6xl xl:text-[4.5rem] leading-[1.04] mb-8 hero-headline">
              Every Occasion Deserves an{' '}
              <em className="italic text-rdr-gold">Extraordinary</em> Setting
            </h1>
            <p className="text-white/70 font-sans text-lg max-w-xl mb-12 leading-relaxed hero-sub">
              Discover what sets Red Door Ranch apart as the premier luxury event venue near Fort Worth, Texas.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 hero-cta">
              <Link
                to="/location#contact"
                className="inline-flex items-center gap-3 bg-rdr-gold text-white font-sans text-xs tracking-[0.25em] uppercase px-10 py-4 hover:bg-rdr-gold-light transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Schedule a Private Tour
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </Link>
              <a
                href="tel:+18175048501"
                className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-sans text-xs tracking-[0.25em] uppercase px-10 py-4 hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-sm" />
                </div>
                (817) 504-8501
              </a>
            </div>
          </div>
          {/* Stats strip */}
          <div className="hero-cta mt-16 lg:mt-20 pt-8 border-t border-white/10 flex flex-wrap items-center gap-8 lg:gap-14">
            {[
              { value: '4', label: 'Event Types' },
              { value: '300', label: 'Max Guests' },
              { value: '6,000+', label: 'Sq Ft Indoor' },
              { value: '1', label: 'Event Per Day' },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-4">
                <span className="font-serif text-white text-3xl font-medium">{s.value}</span>
                <div className="w-px h-8 bg-white/15" />
                <span className="text-white/45 font-sans text-[11px] tracking-[0.2em] uppercase">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <SectionTrustBar items={trustItems} />
      {/* 3. Services Overview */}
      <ServicesDetail />
      {/* 4. AI Answer Block */}
      <SectionAIAnswer heading="Services at a Glance" items={aiAnswers} />
      {/* 5. Process */}
      <SectionProcess heading="How We Work Together" steps={processSteps} />
      {/* 6. Why Choose Us */}
      <ServicesWhyChooseUs />
      {/* 7. Service Areas */}
      <SectionServiceAreas
        areas={serviceAreas}
        description="Whether you're planning a wedding in Joshua, a corporate retreat from Fort Worth, or a private event anywhere in the DFW metroplex — Red Door Ranch is your destination venue, just 20 minutes from the heart of Fort Worth on Chisholm Trail Parkway."
      />
      {/* 8. Testimonials */}
      <SectionTestimonials />
      {/* 9. FAQ */}
      <SectionFAQ heading="Services FAQ" items={servicesFAQs} />
      {/* 10. Final CTA */}
      <SectionFinalCTA
        heading="Ready to See It For Yourself?"
        bgImage="https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/4af24bd9-4fc6-4d89-abe7-e71420a4fc3d/4I6A0440.jpg"
      />
    </main>
  );
}
